
import { useEffect } from "react";
import { Navigate, Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus } from "lucide-react";
import { isUserRegistered, checkTenantExists } from "@/services/authService";
import { toast } from "sonner";

export default function LoginPage() {
  const { user, session, isLoading, authStatus, signInWithMicrosoft } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  
  // IMPORTANT: Moved the conditional navigation below all hooks to avoid React hook errors
  // Now using useEffect for navigation instead of conditional early return
  
  useEffect(() => {
    // If user is already logged in and authorized, redirect
    if (user && authStatus.isAuthorized && !authStatus.isChecking) {
      navigate("/");
    }
  }, [user, authStatus, navigate]);

  // Handle login callback
  useEffect(() => {
    // Check if this is a callback from Microsoft auth
    const isAuthCallback = location.pathname === "/login/callback" || 
                           new URLSearchParams(location.search).has('code');
    
    console.log("LoginPage - Auth callback check:", isAuthCallback, "User:", !!user, "Session:", !!session);
    
    if (isAuthCallback && user && !authStatus.isChecking) {
      console.log("LoginPage - Processing auth callback with user");
      // Check if the user is registered
      isUserRegistered(user.id).then(isRegistered => {
        console.log("LoginPage - User registered:", isRegistered);
        if (!isRegistered) {
          // If not registered, check if the tenant already exists
          const tenantId = user.app_metadata?.tenant_id;
          if (tenantId) {
            checkTenantExists(tenantId).then(tenantExists => {
              console.log("LoginPage - Tenant exists:", tenantExists);
              if (tenantExists) {
                // If tenant already exists, redirect to the error page
                navigate("/error");
              } else {
                // If not, redirect to complete registration
                navigate("/signup/complete");
              }
            });
          } else {
            // If there's no tenant ID, redirect to error page
            toast.error("Missing tenant ID in user metadata");
            navigate("/error");
          }
        } else if (authStatus.isAuthorized) {
          // If registered and authorized, redirect to dashboard
          navigate("/");
        } else {
          // If registered but not authorized, redirect to error page
          navigate("/error");
          toast.error("Your account exists but is not authorized. Please contact your organization administrator.");
        }
      });
    }
  }, [user, authStatus, location, navigate, session]);

  const handleMicrosoftSignIn = async () => {
    try {
      await signInWithMicrosoft( window.location.origin + "/login/callback" );
    } catch (error: any) {
      console.error("Login error:", error);
    }
  };

  // Always render the component, letting the useEffect handle redirects
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-md rounded-lg border bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center">
          <div className="flex h-12 w-12 items-center justify-center">
            <img src="/favicon-324.png" alt="AutoReply365" className="h-12 w-12" />
          </div>
          <h1 className="mt-4 text-2xl font-bold text-primary">AutoReply365</h1>
          <p className="text-center text-sm text-muted-foreground">
            Sign in to access your dashboard
          </p>
        </div>
        
        <div className="space-y-4">
          <Button 
            type="button" 
            className="w-full" 
            onClick={handleMicrosoftSignIn}
            disabled={isLoading}
          >
            <LogIn className="mr-2 h-4 w-4" />
            {isLoading ? "Signing in..." : "Sign in with Microsoft"}
          </Button>
          
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t"></span>
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-white px-2 text-muted-foreground">New to AutoReply365? Sign up now.</span>
            </div>
          </div>
          
          <Button 
            type="button" 
            variant="outline" 
            className="w-full"
            asChild
          >
            <Link to="/signup">
              <UserPlus className="mr-2 h-4 w-4" />
              Create a new account
            </Link>
          </Button>
        </div>
        
        <div className="mt-6 text-center text-sm text-muted-foreground">
          <p>
            AutoReply365 builds upon Microsoft 365. Use your Microsoft account for access.
          </p>
        </div>
      </div>
    </div>
  );
}
